import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { Icono } from '#components/mobile/ui/Icono';
import { color, movimiento } from '#components/mobile/ui/tokens';

import type { NivelAncla } from './automatica';
import { CelebracionCopia } from './CelebracionCopia';
import { HojaCopia } from './HojaCopia';
import { useCopiaAutomatica } from './useCopiaAutomatica';

const LADO = 40;
const GROSOR = 3;

const COLOR_NIVEL: Record<NivelAncla, string> = {
  nube: color.accent,
  bien: color.accent,
  pronto: color.warn,
  mal: color.bad,
};

const girar = keyframes({ to: { transform: 'rotate(270deg)' } });
const claseGirando = css({
  animation: `${girar} 1.1s linear infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/**
 * El ancla de Inicio (arriba a la derecha, a la altura del mes): la copia de
 * seguridad en pequeño. El círculo se llena hasta la siguiente copia
 * automática y cambia a ámbar o rojo solo si algo va mal. Al tocarla se abre
 * la hoja con «Hacer copia ahora» y dónde y cada cuánto se guardan. Mientras
 * está montada, hace la copia sola cuando toca.
 *
 * `sobreCielo`: va encima del cielo de la cabecera (fondo translúcido).
 */
export function AnclaCopia({ sobreCielo = false }: { sobreCielo?: boolean }) {
  const { t } = useTranslation();
  const copia = useCopiaAutomatica({ vigilar: true });
  const [abierta, setAbierta] = useState(false);
  const [celebrando, setCelebrando] = useState(false);
  const terminarCelebracion = useCallback(() => setCelebrando(false), []);

  const { nivel, fraccion, sinElegir } = copia.estado;
  const r = (LADO - GROSOR) / 2;
  const circunferencia = 2 * Math.PI * r;
  const llenado = copia.copiando ? 0.3 : fraccion;
  const icono = nivel === 'mal' ? 'alert' : 'anchor';
  const tinta = sobreCielo ? '#ffffff' : color.fg;
  const etiqueta =
    nivel === 'nube'
      ? t('Backup: in the cloud')
      : nivel === 'bien'
        ? t('Backup: up to date')
        : nivel === 'pronto'
          ? t('Backup: due soon')
          : t('Backup: needs your attention');

  return (
    <View style={{ position: 'relative', flexShrink: 0 }}>
      <Button
        variant="bare"
        aria-label={etiqueta}
        data-testid="ancla-copia"
        data-nivel={nivel}
        onPress={() => setAbierta(true)}
        style={({ isPressed }) => ({
          width: LADO,
          height: LADO,
          minWidth: LADO,
          padding: 0,
          borderRadius: '50%',
          position: 'relative',
          backgroundColor: sobreCielo
            ? 'rgba(255, 255, 255, 0.18)'
            : color.surface,
          backdropFilter: sobreCielo ? 'blur(6px)' : undefined,
          transform: isPressed ? 'scale(0.92)' : undefined,
          transition: `transform ${movimiento.pulsar}ms ${movimiento.muelle}`,
        })}
      >
        <svg
          width={LADO}
          height={LADO}
          viewBox={`0 0 ${LADO} ${LADO}`}
          className={copia.copiando ? claseGirando : undefined}
          style={{
            position: 'absolute',
            inset: 0,
            transform: 'rotate(-90deg)',
          }}
          aria-hidden
        >
          <circle
            cx={LADO / 2}
            cy={LADO / 2}
            r={r}
            fill="none"
            stroke={sobreCielo ? 'rgba(255, 255, 255, 0.28)' : color.surface3}
            strokeWidth={GROSOR}
          />
          <circle
            cx={LADO / 2}
            cy={LADO / 2}
            r={r}
            fill="none"
            stroke={COLOR_NIVEL[nivel]}
            strokeWidth={GROSOR}
            strokeLinecap="round"
            strokeDasharray={`${circunferencia * llenado} ${circunferencia}`}
            style={{ transition: 'stroke-dasharray 600ms ease, stroke 180ms' }}
          />
        </svg>
        <span style={{ color: nivel === 'mal' ? color.bad : tinta }}>
          <Icono nombre={icono} size={18} />
        </span>
        {(sinElegir || nivel === 'mal') && (
          <span
            data-testid="ancla-punto"
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: 11,
              height: 11,
              borderRadius: '50%',
              backgroundColor: nivel === 'mal' ? color.bad : color.warn,
              border: `2px solid ${color.bg}`,
            }}
          />
        )}
      </Button>
      {celebrando && (
        <CelebracionCopia tamano={LADO} onFin={terminarCelebracion} />
      )}
      {abierta && (
        <HojaCopia
          copia={copia}
          onCerrar={() => setAbierta(false)}
          onHecha={() => setCelebrando(true)}
        />
      )}
    </View>
  );
}
